/**
 * TanStackTable Component
 *
 * A full-featured data table built on TanStack Table with:
 * - Column resizing, sorting, filtering
 * - Drag-and-drop column reordering
 * - Column and row pinning
 * - Virtualization support for large datasets
 * - Persistent state via localStorage
 */
import { useTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { ActiveSortConfig } from "@/shared/hooks/useSorting";
import TanStackColumnsSelector from "./TanStackColumnsSelector";
import { TableContainer, TableTopBar, TableContent } from "./components";
import { useTableState, useTableRendering } from "./hooks";
import {
  processTableColumns,
  createTableConfig,
  createTableState,
  createTableHandlers,
} from "./utils";
import type { TanStackTableProps, TableBodyProps } from "./types";
import type { RowData } from "@tanstack/react-table";

// Stable empty array reference to prevent infinite re-renders when activeSorts is undefined
const EMPTY_ACTIVE_SORTS: ActiveSortConfig[] = [];

/**
 * Main hook that orchestrates all table logic
 */
const useCompleteTableLogic = <T extends RowData>(params: {
  columns: TanStackTableProps<T>["columns"]
  data: T[]
  storageKey?: string
  initialColumnPinning?: TanStackTableProps<T>["initialColumnPinning"]
  initialColumnOrder?: TanStackTableProps<T>["initialColumnOrder"]
  initialRowPinning?: TanStackTableProps<T>["initialRowPinning"]
  enableColumnResizing: boolean
  enableColumnPinning: boolean
  enableColumnFiltering: boolean
  enableColumnOrdering: boolean
  enableRowPinning: boolean
  enableSorting: boolean
  manualSorting: boolean
  maxMultiSortColCount: number
  enableSortingRemoval: boolean
  isMultiSortEvent: () => boolean
  onColumnPinningChange?: TanStackTableProps<T>["onColumnPinningChange"]
  onColumnOrderChange?: TanStackTableProps<T>["onColumnOrderChange"]
  onRowPinningChange?: TanStackTableProps<T>["onRowPinningChange"]
  onColumnVisibilityChange?: TanStackTableProps<T>["onColumnVisibilityChange"]
  isLoading: boolean
  isLoadingMore: boolean
  skeletonRowCount: number
  onRowClick?: TanStackTableProps<T>["onRowClick"]
  getRowClassName?: TanStackTableProps<T>["getRowClassName"]
  isHighlightedRow?: TanStackTableProps<T>["isHighlightedRow"]
  highlightedRowId?: string
  tooltipClassName: string
  virtualRowHeight: number
  virtualOverscan: number
  metadata?: TanStackTableProps<T>["metadata"]
  fitToContainer?: boolean
  activeSorts?: ActiveSortConfig[]
  renderExpandedRow?: TanStackTableProps<T>["renderExpandedRow"]
  expandedRowIds?: TanStackTableProps<T>["expandedRowIds"]
  getRowId?: TanStackTableProps<T>["getRowId"]
  fetchNextPage?: () => Promise<unknown>
  hasNextPage?: boolean
  getRowProps?: TanStackTableProps<T>["getRowProps"]
  memoizeRows: boolean
}) => {
  const tableState = useTableState({
    storageKey: params.storageKey,
    initialColumnPinning: params.initialColumnPinning,
    initialColumnOrder: params.initialColumnOrder,
    initialRowPinning: params.initialRowPinning,
    columns: params.columns,
    activeSorts: params.activeSorts,
  });

  const processedColumns = useMemo(
    () => processTableColumns(
      params.columns,
      params.enableColumnResizing,
      params.enableColumnPinning,
      params.enableColumnFiltering,
      params.enableColumnOrdering,
    ),
    [
      params.columns,
      params.enableColumnResizing,
      params.enableColumnPinning,
      params.enableColumnFiltering,
      params.enableColumnOrdering,
    ],
  );

  const stateObject = createTableState(
    tableState.sorting,
    tableState.columnFilters,
    tableState.columnVisibility,
    tableState.columnSizing,
    params.enableColumnPinning,
    tableState.columnPinning,
    params.enableColumnOrdering,
    tableState.columnOrder,
    params.enableRowPinning,
    tableState.rowPinning,
  );

  const handlersObject = createTableHandlers(
    tableState.setSorting,
    tableState.setColumnFilters,
    tableState.setColumnVisibility,
    tableState.setColumnSizing,
    params.enableColumnPinning,
    params.onColumnPinningChange,
    tableState.setColumnPinning,
    params.enableColumnOrdering,
    params.onColumnOrderChange,
    tableState.setColumnOrder,
    params.enableRowPinning,
    params.onRowPinningChange,
    tableState.setRowPinning,
  );

  const table = useTable(
    createTableConfig(
      params.data,
      processedColumns,
      stateObject,
      handlersObject,
      params.enableColumnResizing,
      params.enableSorting,
      params.enableColumnPinning,
      params.enableColumnOrdering,
      params.enableRowPinning,
      params.maxMultiSortColCount,
      params.enableSortingRemoval,
      params.isMultiSortEvent,
      params.manualSorting,
    ),
  );

  const rendering = useTableRendering(
    table,
    params.metadata,
    params.isLoading,
    params.onColumnVisibilityChange,
    params.storageKey,
    params.fitToContainer,
  );

  const tableBodyProps: TableBodyProps<T> = useMemo(() => ({
    hasNoVisibleColumns: rendering.hasNoVisibleColumns,
    columns: params.columns,
    isLoading: params.isLoading,
    data: params.data,
    isLoadingMore: params.isLoadingMore,
    skeletonRowCount: params.skeletonRowCount,
    visibleColumns: rendering.visibleColumns,
    table,
    onRowClick: params.onRowClick,
    getRowClassName: params.getRowClassName,
    isHighlightedRow: params.isHighlightedRow,
    highlightedRowId: params.highlightedRowId,
    tooltipClassName: params.tooltipClassName ?? "max-w-[400px]",
    virtualRowHeight: params.virtualRowHeight ?? 52,
    virtualOverscan: params.virtualOverscan ?? 5,
    enableColumnPinning: params.enableColumnPinning,
    renderExpandedRow: params.renderExpandedRow,
    expandedRowIds: params.expandedRowIds,
    getRowId: params.getRowId,
    fetchNextPage: params.fetchNextPage,
    hasNextPage: params.hasNextPage,
    getRowProps: params.getRowProps,
    memoizeRows: params.memoizeRows,
  }), [
    rendering.hasNoVisibleColumns,
    params.columns,
    params.isLoading,
    params.data,
    params.isLoadingMore,
    params.skeletonRowCount,
    rendering.visibleColumns,
    table,
    params.onRowClick,
    params.getRowClassName,
    params.isHighlightedRow,
    params.highlightedRowId,
    params.tooltipClassName,
    params.virtualRowHeight,
    params.virtualOverscan,
    params.enableColumnPinning,
    params.renderExpandedRow,
    params.expandedRowIds,
    params.getRowId,
    params.fetchNextPage,
    params.hasNextPage,
    params.getRowProps,
    params.memoizeRows,
  ]);

  return {
    table,
    tableState,
    rendering,
    tableBodyProps,
  };
};

/**
 * Build normalized props with defaults
 */
const buildLogicProps = <T extends RowData>(props: TanStackTableProps<T>) => ({
  columns: props.columns,
  data: props.data,
  storageKey: props.storageKey,
  initialColumnPinning: props.initialColumnPinning,
  initialColumnOrder: props.initialColumnOrder,
  initialRowPinning: props.initialRowPinning,
  enableColumnResizing: props.enableColumnResizing ?? true,
  enableColumnPinning: props.enableColumnPinning ?? false,
  enableColumnFiltering: props.enableColumnFiltering ?? false,
  enableColumnOrdering: props.enableColumnOrdering ?? false,
  enableRowPinning: props.enableRowPinning ?? false,
  enableSorting: props.enableSorting ?? true,
  manualSorting: props.manualSorting ?? false,
  maxMultiSortColCount: props.maxMultiSortColCount ?? 2,
  enableSortingRemoval: props.enableSortingRemoval ?? true,
  isMultiSortEvent: () => props.isMultiSortEvent?.() ?? true,
  onColumnPinningChange: props.onColumnPinningChange,
  onColumnOrderChange: props.onColumnOrderChange,
  onRowPinningChange: props.onRowPinningChange,
  onColumnVisibilityChange: props.onColumnVisibilityChange,
  isLoading: props.isLoading ?? false,
  isLoadingMore: props.isLoadingMore ?? false,
  skeletonRowCount: props.skeletonRowCount ?? 10,
  onRowClick: props.onRowClick,
  getRowClassName: props.getRowClassName,
  isHighlightedRow: props.isHighlightedRow,
  highlightedRowId: props.highlightedRowId,
  tooltipClassName: props.tooltipClassName ?? "max-w-[400px]",
  virtualRowHeight: props.virtualRowHeight ?? 52,
  virtualOverscan: props.virtualOverscan ?? 5,
  metadata: props.metadata,
  fitToContainer: props.fitToContainer ?? false,
  activeSorts: props.activeSorts ?? EMPTY_ACTIVE_SORTS,
  onChangeSort: props.onChangeSort,
  fetchNextPage: props.fetchNextPage,
  hasNextPage: props.hasNextPage,
  renderExpandedRow: props.renderExpandedRow,
  expandedRowIds: props.expandedRowIds,
  getRowId: props.getRowId,
  getRowProps: props.getRowProps,
  memoizeRows: props.memoizeRows ?? false,
});

/**
 * Main TanStackTable component
 */
export const TanStackTable = <T extends RowData>(props: TanStackTableProps<T>) => {
  const logic = useCompleteTableLogic(buildLogicProps(props));
  const {
    className,
    data: _data,
    columns: _columns,
    onRowClick: _onRowClick,
    isLoading: _isLoading,
    isLoadingMore: _isLoadingMore,
    skeletonRowCount: _skeletonRowCount,
    truncateClassName: _truncateClassName,
    tooltipClassName: _tooltipClassName,
    metadata: _metadata,
    showTotalAmount: _showTotalAmount,
    counterType: _counterType,
    counterSize: _counterSize,
    fitToContainer: _fitToContainer,
    count: _count,
    isHighlightedRow: _isHighlightedRow,
    highlightedRowId: _highlightedRowId,
    getRowClassName: _getRowClassName,
    storageKey: _storageKey,
    onColumnVisibilityChange: _onColumnVisibilityChange,
    enableColumnResizing: _enableColumnResizing,
    enableSorting: _enableSorting,
    enableVirtualization: _enableVirtualization,
    virtualRowHeight: _virtualRowHeight,
    virtualOverscan: _virtualOverscan,
    enableColumnPinning: _enableColumnPinning,
    enableColumnOrdering: _enableColumnOrdering,
    enableRowPinning: _enableRowPinning,
    enableColumnFiltering: _enableColumnFiltering,
    manualSorting: _manualSorting,
    initialColumnPinning: _initialColumnPinning,
    initialColumnOrder: _initialColumnOrder,
    initialRowPinning: _initialRowPinning,
    onColumnPinningChange: _onColumnPinningChange,
    onColumnOrderChange: _onColumnOrderChange,
    onRowPinningChange: _onRowPinningChange,
    activeSorts: _activeSorts,
    onRemoveSort: _onRemoveSort,
    onChangeSort: _onChangeSort,
    fetchNextPage: _fetchNextPage,
    hasNextPage: _hasNextPage,
    maxMultiSortColCount: _maxMultiSortColCount,
    enableSortingRemoval: _enableSortingRemoval,
    isMultiSortEvent: _isMultiSortEvent,
    renderExpandedRow: _renderExpandedRow,
    expandedRowIds: _expandedRowIds,
    getRowId: _getRowId,
    getRowProps: _getRowProps,
    memoizeRows: _memoizeRows,
    toolbarActions: _toolbarActions,
    ...restProps
  } = props;

  return (
    <div {...restProps}>
      <TableTopBar
        isLoading={props.isLoading ?? false}
        counterType={props.counterType ?? "résultat"}
        counterSize={props.counterSize ?? "large"}
        metadata={props.metadata}
        showTotalAmount={props.showTotalAmount}
        hideCounter={props.hideCounter}
        activeSorts={props.activeSorts ?? []}
        onRemoveSort={props.onRemoveSort}
        manualSorting={props.manualSorting}
        toolbarActions={props.toolbarActions}
        columnsSelector={
          props.storageKey
            ? (
              <TanStackColumnsSelector
                table={logic.table}
                columns={props.columns}
              />
            )
            : undefined
        }
      />
      <TableContainer className={className}>
        <TableContent
          enableVirtualization={props.enableVirtualization ?? false}
          tableStyle={logic.rendering.tableStyle}
          table={logic.table}
          hasNoVisibleColumns={logic.rendering.hasNoVisibleColumns}
          enableSorting={props.enableSorting ?? true}
          onChangeSort={props.onChangeSort}
          enableColumnResizing={props.enableColumnResizing ?? true}
          enableColumnPinning={props.enableColumnPinning ?? false}
          enableColumnOrdering={props.enableColumnOrdering ?? false}
          enableColumnFiltering={props.enableColumnFiltering ?? false}
          tableBodyProps={logic.tableBodyProps}
          data={props.data}
        />
      </TableContainer>
      {props.footer && (
        <>
          {/* Spacer so the last row isn't hidden by the sticky footer on infinite scroll */}
          <div aria-hidden className="h-14" />
          <div className="sticky bottom-0 z-20 rounded-lg border-2 border-slate-300 bg-white shadow-[0_-6px_16px_-4px_rgba(0,0,0,0.18)]">
            {props.footer}
          </div>
        </>
      )}
    </div>
  );
};
